import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Center, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { Modal } from "../../../components/core/basic/Modal";
import { errorToast, successToast } from "../../../utils/toasts";
import { useRemoveProductsFromPlan } from "../api/useRemoveProductsFromPlan";

interface Props {
  selectedProducts: number[];
  productById: Record<number, { sku: string }>;
  onRemove: () => void;
}

const RemoveProductsFromPlanModal = NiceModal.create(({ selectedProducts, productById, onRemove }: Props) => {
  const { t } = useTranslation(["pricingv2", "common"]);
  const modal = useModal();
  const [isConfirming, setIsConfirming] = useState(false);
  const removeProductsMutation = useRemoveProductsFromPlan();

  return (
    <Modal
      isOpen={modal.visible}
      isDismissable
      onOpenChange={(isOpen) => (isOpen ? void 0 : modal.remove())}
      title={t(($) => $.pricingv2["Remove Products from Pricing Plans"])}
    >
      <VStack paddingX="large">
        {isConfirming && <p>{t(($) => $.pricingv2["The products will be removed"])}</p>}

        {!isConfirming && (
          <>
            <h4 className={css({ margin: 0 })}>
              {t(($) => $.pricingv2["You will remove these products from their active pricing plans."])}
            </h4>

            <ul>
              {selectedProducts.slice(0, 5).map((id, i) => (
                <li key={i}>{productById[id]?.sku}</li>
              ))}
              {selectedProducts.length > 5 && (
                <span> {t(($) => $.pricingv2["plus more"], { count: selectedProducts.length - 5 })}</span>
              )}
            </ul>

            <p>{t(($) => $.pricingv2["Do you want to continue?"])}</p>
          </>
        )}
        <Center gap="medium">
          <Button variant="secondary" onPress={() => modal.remove()}>
            {t(($) => $.common.actions.Cancel)}
          </Button>

          <Button
            isPending={removeProductsMutation.isPending}
            onPress={() => {
              if (isConfirming) {
                removeProductsMutation.mutate(
                  { product_ids: selectedProducts },
                  {
                    onSuccess: () => {
                      onRemove();
                      successToast({
                        title: t(($) => $.pricingv2["Products removed"]),
                        message: t(($) => $.pricingv2["The products have been removed from their pricing plans."]),
                      });
                      modal.remove();
                    },
                    onError: (error) => {
                      errorToast({
                        title: t(($) => $.pricingv2["Error removing products"]),
                        error,
                      });
                    },
                  },
                );
              } else {
                setIsConfirming(true);
              }
            }}
          >
            {t(($) => $.pricingv2["Remove Products"])}
          </Button>
        </Center>
      </VStack>
    </Modal>
  );
});

export default RemoveProductsFromPlanModal;
